/**
 * Hero composition markers — the OPEN, data-authored mechanic space (D-24031).
 *
 * The OPEN half of the composable-primitive model (D-24029): a card MECHANIC (Berserk)
 * is a composition of primitives expressed as DATA, distinct from the closed primitive
 * registry (D-24030, `effectPrimitive.types.ts`). The setup parser recognizes a
 * `[keyword:X]` token whose normalized name is a key here and attaches a deep copy of its
 * AST to `HeroAbilityHook.primitiveEffects` — never to `hook.keywords` (`berserk` is not a
 * HeroKeyword). The coverage probe + mechanic ledger read this surface to recognize
 * composition markers without duplicating the vocabulary.
 *
 * Adding a mechanically-adjacent mechanic (the Recruit-stat Berserk cousin) is a new ROW
 * here — no new keyword, primitive, handler, union edit, or D-entry. Premature-abstraction
 * counter-pressure (D-24029 §10): only `berserk` is seeded this WP.
 *
 * No boardgame.io imports. No registry imports. Data only.
 */
// why: D-24031 — the open mechanic-space data surface the setup parser, coverage probe,
// and mechanic ledger read. Maps a card marker name to a top-level composition AST. A
// cousin is a new row here, never an engine edit (D-24029 §7 / §10).
/**
 * Card marker name → its top-level effect composition. Seeded with exactly one entry.
 */
export const HERO_COMPOSITION_MARKERS = {
    // why: Berserk (data/metadata/keywords-full.json) — "Discard the top card of your deck.
    // You get +Attack equal to the discarded card's printed Attack." A sequence: move the
    // deck-top card to the discard (binding its id), then gain attack equal to that card's
    // printed attack (read from the binding via card-printed-stat). The Recruit cousin is
    // the same shape with resource:'recruit' + stat:'recruit' — pure data, no engine edit.
    berserk: {
        type: 'sequence',
        steps: [
            {
                type: 'move-card',
                from: { owner: 'current-player', zone: 'deck', position: 'top' },
                to: { owner: 'current-player', zone: 'discard' },
                bind: 'discardedCard',
            },
            {
                type: 'gain-resource',
                resource: 'attack',
                amount: { type: 'card-printed-stat', card: { ref: 'discardedCard' }, stat: 'attack' },
            },
        ],
    },
};
// why: D-24044 — the first PARAMETERIZED composition marker. Unlike a static
// HERO_COMPOSITION_MARKERS row, Empowered's AST depends on a hero class parsed from the card
// text, so it is BUILT per parsed class rather than stored as a fixed row. The builder + a
// mechanic-specific parse step (heroAbility.setup.ts) is the parameterized analog of a static
// row; the interpreter stays mechanic-agnostic (it knows only the value expression).
/**
 * Builds the Empowered composition for one parsed hero class: gain +Attack equal to the
 * number of that-class cards currently in the HQ. Returns a FRESHLY-constructed node each
 * call (never a shared mutable singleton), so a parsed hook never aliases module state.
 *
 * @param heroClass - The normalized hero-class slug parsed from `Empowered by [hc:X]`.
 * @returns A `gain-resource` effect node granting +Attack by the HQ class count.
 */
export function buildEmpoweredComposition(heroClass) {
    return {
        type: 'gain-resource',
        resource: 'attack',
        amount: { type: 'count-cards-by-class-in-zone', heroClass, zone: 'hq' },
    };
}
// why: D-24063 — free-choice resolved as oracle-max over all classes in HQ; `classes: 'all'`
// defers to the evaluator to scan the full HQ without naming a specific class.
/**
 * Builds the free-choice Empowered composition: gain +Attack equal to the highest class
 * count across ALL hero classes in the HQ. Used when the card text has no `[hc:CLASS]`
 * literal ("by the color of your choice"). Returns a FRESHLY-constructed node each call.
 *
 * @returns A `gain-resource` effect node granting +Attack by the oracle-max of all HQ classes.
 */
export function buildEmpoweredFreeChoiceComposition() {
    return {
        type: 'gain-resource',
        resource: 'attack',
        amount: { type: 'max-class-count-in-zone', classes: 'all', zone: 'hq' },
    };
}
// why: D-24063 — choose-one resolved as oracle-max of the two enumerated classes; one
// composition for the whole line (not one per [keyword:Empowered] token).
/**
 * Builds the choose-one Empowered composition: gain +Attack equal to the highest count
 * among the enumerated hero classes in the HQ. Used when the card text lists two explicit
 * class choices. Returns a FRESHLY-constructed node each call (never a shared singleton),
 * so a parsed hook never aliases module state.
 *
 * @param classes - The normalized hero-class slugs extracted from the choose-one line.
 * @returns A `gain-resource` effect node granting +Attack by the oracle-max of the listed classes.
 */
export function buildEmpoweredChooseOneComposition(classes) {
    return {
        type: 'gain-resource',
        resource: 'attack',
        // why: spread into a fresh array so the caller's local list never aliases the AST field.
        amount: { type: 'max-class-count-in-zone', classes: [...classes], zone: 'hq' },
    };
}
// why: D-24065 — dynamic-empowered deck-peek form (cross-the-multiverse); the class is NOT a
// static literal but the class of the top card of the player's deck at runtime. The evaluator
// peeks deck[0] and counts matching HQ cards — no zone move (D-24065 determinism guarantee).
/**
 * Builds the dynamic Empowered composition: gain +Attack equal to the number of HQ cards
 * sharing the hero class of the top card of the active player's deck. Used when the card
 * text references a runtime-dynamic class ("by the Hero Classes of the card you revealed
 * this way"). Returns a FRESHLY-constructed node each call (never a shared singleton).
 *
 * @returns A `gain-resource` effect node granting +Attack by the deck-top card's class count in HQ.
 */
export function buildDynamicEmpoweredComposition() {
    return {
        type: 'gain-resource',
        resource: 'attack',
        amount: { type: 'top-deck-card-class-count-in-zone', zone: 'hq' },
    };
}
// why: D-24044 — parameterized composition marker names: recognized mechanics whose AST is
// built (not a static HERO_COMPOSITION_MARKERS row). Seeded with exactly `empowered`. The
// coverage probe + mechanic ledger read these via HERO_COMPOSITION_MARKER_NAMES below, so the
// marker leaves the unsupported list while the parser builds the per-class AST.
/**
 * All parameterized composition marker names (built per parsed parameter, not stored rows).
 */
export const PARAMETERIZED_COMPOSITION_MARKER_NAMES = ['empowered'];
// why: D-24031 / D-24044 — the canonical key array the coverage probe + mechanic ledger import
// (from dist) to recognize BOTH static composition markers (HERO_COMPOSITION_MARKERS keys) and
// parameterized ones (PARAMETERIZED_COMPOSITION_MARKER_NAMES). Deduped via a Set so a name
// migrating between the two surfaces never double-lists.
/**
 * All hero composition marker names (static + parameterized), deduped. Single source of truth.
 */
export const HERO_COMPOSITION_MARKER_NAMES = [
    ...new Set([
        ...Object.keys(HERO_COMPOSITION_MARKERS),
        ...PARAMETERIZED_COMPOSITION_MARKER_NAMES,
    ]),
];
//# sourceMappingURL=heroCompositions.js.map